import FilterListIcon from '@material-ui/icons/FilterList'
import { makeStyles } from '@material-ui/styles'
import FetchTable from 'app/components/table/FetchTable'
import { dateFormat, getLocalizedDate } from 'commons/date'
import moment from 'moment'
import * as R from 'ramda'
import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Chip, Tooltip } from '@material-ui/core'
import clsx from 'clsx'
import { removeHTMLTags } from 'libs/text'

import { FilterList } from './DealsPromotionsFilterList'
import DealPromotionRowMenu from './DealPromotionRowMenu'
import ConfirmationDialog from 'app/components/ConfirmationDialog'
import SecondaryButton from 'app/components/buttons/SecondaryButton'
import PrimaryButton from 'app/components/buttons/PrimaryButton'

const useStyles = makeStyles((theme) => ({
  datePicker: {
    '& .MuiInputBase-root': {
      color: '#0073b1',
      'border-radius': '20px',
      'background-color': '#e5f1f7',
    },
    '& input': {
      padding: '9px 32px 9px 16px',
      'font-size': '12px',
      cursor: 'pointer',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .material-icons': {
      top: '10px',
      right: '13px',
    },
  },
  statusSelect: {
    paddingTop: 1,
    fontSize: 12,
    color: '#404040',
    '&::before': {
      display: 'none',
    },
    '&::after': {
      display: 'none',
    },
    '& .MuiSelect-select': {
      color: '#0073b1',
      padding: '9px 32px 9px 16px',
      backgroundColor: '#e5f1f7',
      borderRadius: 20,
    },
  },
  selectIcon: {
    position: 'absolute',
    right: 10,
    pointerEvents: 'none',
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0073b1',
  },
  active: {
    color: '#fff',
    backgroundColor: '#4fd975',
  },
  fully_redeemed: {
    color: '#fff',
    backgroundColor: '#1ec4db',
  },
  canceled: {
    color: '#fff',
    backgroundColor: '#f74872',
  },
  expired: {
    color: '#f74872',
    backgroundColor: '#e5e5e5',
  },
}))

const VOUCHER_STATUS = {
  1: 'active',
  2: 'fully_redeemed',
  3: 'canceled',
  4: 'expired',
}

const VOUCHER_STATUS_MSG = {
  1: 'dealsPromotions.status.active',
  2: 'dealsPromotions.status.fullyRedeemed',
  3: 'dealsPromotions.status.canceled',
  4: 'dealsPromotions.status.expired',
}

const initDialogState = {
  open: false,
  data: null,
}

const DealsPromotionsTable = ({
  pageInfo,
  onFetchData,
  onSortedChange,
  onPageSizeChange,
  refetchData,
  startFetchData,
  endFetchData,
  onPageChange,
  showPagination,
  onChangeFilterByKey,
  defaultPageSize,
  filter,
  handleClickEditData,
  handleDeleteOne,
}) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const [confirmDialogState, setConfirmDialogState] = useState(initDialogState)

  const StatusChips = (props, classes, formatMessage) => {
    return (
      <Chip
        className={clsx(
          classes.chipStyles,
          classes[VOUCHER_STATUS[props.value]],
        )}
        label={formatMessage({
          id: VOUCHER_STATUS_MSG[props.value],
        })}
      ></Chip>
    )
  }
  const menuDealPromotion = (props) => {
    return (
      VOUCHER_STATUS[props.original.status] === 'active' && (
        <div>
          <DealPromotionRowMenu
            onMenuCancel={() => {
              handleDeleteDeal(props)
            }}
            data={props.original}
          />
        </div>
      )
    )
  }
  const handleDeleteDeal = (data) => {
    setConfirmDialogState({ data: data.original, open: true })
  }

  const decodeHtmlEntity = (rawHTML) =>
    React.createElement('div', {
      dangerouslySetInnerHTML: { __html: rawHTML },
    })

  return (
    <div className="flex flex-col flex-auto overflow-hidden">
      {!pageInfo.error && (
        <div>
          <div className="flex flex-col flex-auto overflow-hidden items-stretch sm:flex-row sm:items-center mb-16">
            <FilterListIcon className="mr-8" />
            <FilterList
              classes={classes}
              filter={filter}
              onChangeFilterByKey={onChangeFilterByKey}
            />
          </div>
        </div>
      )}
      <ConfirmationDialog
        title={formatMessage({
          id: 'dealsPromotions.table.confirm.delete.title',
        })}
        body={formatMessage({
          id: 'dealsPromotions.table.confirm.delete.body',
        })}
        open={confirmDialogState.open || false}
        CancelButton={
          <SecondaryButton
            onClick={() => {
              setConfirmDialogState({ ...confirmDialogState, open: false })
            }}
          >
            <FormattedMessage id="dealsPromotions.table.confirm.delete.decline" />
          </SecondaryButton>
        }
        ConfirmButton={
          <PrimaryButton
            onClick={() => {
              handleDeleteOne(R.path(['data', 'id'], confirmDialogState)).then(
                (resp) => {
                  refetchData()
                },
              )
              setConfirmDialogState({ ...confirmDialogState, open: false })
            }}
          >
            <FormattedMessage
              className="uppercase"
              id="dealsPromotions.table.confirm.delete.accept"
            />
          </PrimaryButton>
        }
      />

      <FetchTable
        onClickRow={handleClickEditData}
        loading={pageInfo.isLoading}
        FilterComponent={null}
        data={pageInfo.data}
        total={pageInfo.totalData}
        error={pageInfo.error}
        refetchData={refetchData}
        page={pageInfo.page}
        onSortedChange={onSortedChange}
        onPageSizeChange={onPageSizeChange}
        pages={pageInfo.total}
        manual
        onPageChange={onPageChange}
        defaultPageSize={defaultPageSize}
        noDataText={formatMessage({
          id: 'dealsPromotions.table.search.notFound',
        })}
        showPagination={showPagination}
        columns={[
          {
            Header: formatMessage({
              id: 'dealsPromotions.table.notificationTitle',
            }),
            accessor: 'title',
            id: 'title',
            filterable: true,
            minWidth: 150,
            sortable: false,
            type: 'text',
            Cell: (props) => {
              return props.value
                ? decodeHtmlEntity(removeHTMLTags(props.value))
                : ''
            },
          },
          {
            Header: formatMessage({ id: 'dealsPromotions.table.startDate' }),
            accessor: 'startDate',
            sortable: true,
            filterable: true,
            minWidth: 150,
            type: 'text',
            Cell: (props) =>
              props.value ? getLocalizedDate(props.value, 'DATE_FULL') : '-',
          },
          {
            Header: formatMessage({ id: 'dealsPromotions.table.endDate' }),
            accessor: 'endDate',
            sortable: true,
            filterable: true,
            minWidth: 150,
            type: 'text',
            Cell: (props) =>
              props.value ? getLocalizedDate(props.value, 'DATE_FULL') : '-',
          },
          {
            Header: formatMessage({
              id: 'dealsPromotions.table.limitedAmount',
            }),
            accessor: 'limitedAmount',
            id: 'limited_amount',
            sortable: true,
            filterable: true,
            minWidth: 70,
            type: 'text',
            Cell: (props) => props.value.toLocaleString(),
          },
          // {
          //   Header: formatMessage({
          //     id: 'dealsPromotions.table.delivered',
          //   }),
          //   accessor: 'delivered',
          //   id: 'delivery_count',
          //   sortable: true,
          //   filterable: true,
          //   minWidth: 80,
          //   type: 'text',
          //   Cell: (props) => props.value.toLocaleString(),
          // },
          {
            Header: formatMessage({
              id: 'dealsPromotions.table.saved',
            }),
            accessor: 'saved',
            id: 'saved',
            sortable: true,
            filterable: true,
            minWidth: 70,
            type: 'text',
            Cell: (props) => props.value.toLocaleString(),
          },
          {
            Header: formatMessage({ id: 'dealsPromotions.table.used' }),
            accessor: 'used',
            id: 'use_count',
            sortable: true,
            filterable: true,
            minWidth: 70,
            type: 'text',
            Cell: (props) => props.value.toLocaleString(),
          },
          {
            Header: formatMessage({ id: 'subscriber.status.rejected' }),
            accessor: 'rejected',
            id: 'rejected',
            sortable: true,
            filterable: true,
            minWidth: 80,
            type: 'text',
            Cell: (props) => props.value.toLocaleString(),
          },
          // {
          //   Header: () => (
          //     <div className="flex justify-between items-center">
          //       <FormattedMessage id="dealsPromotions.table.conversion" />
          //       <Tooltip
          //         title={formatMessage({
          //           id: 'dealsAndPromotions.conversion.tooltip.text',
          //         })}
          //         placement="bottom"
          //       >
          //         <img
          //           src={Assets.tips.alert}
          //           width={25}
          //           height={25}
          //           alt="info-only-type"
          //         />
          //       </Tooltip>
          //     </div>
          //   ),
          //   accessor: 'conversionRate',
          //   id: 'conversion_count',
          //   sortable: true,
          //   filterable: true,
          //   minWidth: 150,
          //   type: 'text',
          //   Cell: (props) =>
          //     formatMessage(
          //       { id: 'dealsPromotions.table.conversion.value' },
          //       {
          //         value: Number(props.value),
          //       },
          //     ),
          // },
          {
            Header: formatMessage({ id: 'text.status' }),
            accessor: 'status',
            id: 'status',
            minWidth: 150,
            sortable: false,
            Cell: (props) => {
              return StatusChips(props, classes, formatMessage)
            },
          },
          {
            accessor: 'id',
            Cell: (props) => {
              return menuDealPromotion(props)
            },
          },
        ]}
      />
    </div>
  )
}

export default DealsPromotionsTable
